// BEGIN FILE: src/utils/chartDataCache.ts
// Helper functions for persisting and retrieving chart data from Supabase
// Implements single source of truth pattern for chart calculations

import { supabase } from './supabaseClient';
import type { ChartData } from '../types/chartData';
import { isChartDataValid, isChartDataCurrent, isChartDataFresh } from '../types/chartData';

/**
 * Save complete chart data to birth_profiles.chart_data
 * This is the single source of truth for all chart-related calculations
 * 
 * @param profileId - UUID of birth profile (not 'my_profile')
 * @param chartData - Complete calculated chart data
 * @returns Success status and any error
 */
export async function saveChartDataToProfile(
  profileId: string,
  chartData: ChartData
): Promise<{ success: boolean; error?: string }> {
  try {
    // Validate profile ID
    if (!profileId || profileId === 'my_profile') {
      return { 
        success: false, 
        error: 'Invalid profile ID: must be a UUID, not "my_profile"' 
      };
    }

    // Validate chart data structure
    if (!isChartDataValid(chartData)) {
      return { 
        success: false, 
        error: 'Invalid chart data structure: missing required fields' 
      };
    }

    // Save to database
    const { error } = await supabase
      .from('birth_profiles')
      .update({
        chart_data: chartData,
        chart_calculated_at: new Date().toISOString()
      })
      .eq('id', profileId);

    if (error) {
      console.error('[ChartDataCache] Error saving chart data:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('[ChartDataCache] Exception saving chart data:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}

/**
 * Load chart data from birth_profiles.chart_data
 * Returns null if cache is invalid, outdated, or missing
 * 
 * @param profileId - UUID of birth profile
 * @param maxAgeHours - Maximum age of cache in hours (default: effectively infinite for natal charts)
 * @returns Cached chart data or null if invalid/missing
 */
export async function loadChartDataFromProfile(
  profileId: string,
  maxAgeHours: number = 24 * 365  // 1 year default (natal charts are static)
): Promise<ChartData | null> {
  try {
    // Validate profile ID
    if (!profileId || profileId === 'my_profile') {
      console.warn('[ChartDataCache] Invalid profile ID:', profileId);
      return null;
    }

    // Load from database
    const { data, error } = await supabase
      .from('birth_profiles')
      .select('chart_data, chart_calculated_at')
      .eq('id', profileId)
      .single();

    if (error) {
      console.error('[ChartDataCache] Error loading chart data:', error);
      return null;
    }

    if (!data) {
      console.warn('[ChartDataCache] Profile not found:', profileId);
      return null;
    }

    // Check if cache exists
    if (!data.chart_data) {
      return null;
    }

    // Validate structure
    if (!isChartDataValid(data.chart_data)) {
      console.warn('[ChartDataCache] Invalid cached data structure, ignoring cache');
      return null;
    }

    const chartData = data.chart_data as ChartData;

    // Check schema version
    if (!isChartDataCurrent(chartData)) {
      console.warn('[ChartDataCache] Outdated schema version:', chartData.version, '- recalculation needed');
      return null;
    }

    // Check freshness (for safety, though natal charts are static)
    if (!isChartDataFresh(chartData.calculated_at, maxAgeHours)) {
      console.warn('[ChartDataCache] Cache too old:', chartData.calculated_at);
      return null;
    }

    return chartData;
  } catch (err: any) {
    console.error('[ChartDataCache] Exception loading chart data:', err);
    return null;
  }
}

/**
 * Clear cached chart data (force recalculation on next load)
 * Use when birth data changes or user requests recalculation
 * 
 * @param profileId - UUID of birth profile
 * @returns Success status and any error
 */
export async function clearChartDataCache(
  profileId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!profileId || profileId === 'my_profile') {
      return { 
        success: false, 
        error: 'Invalid profile ID' 
      };
    }

    const { error } = await supabase
      .from('birth_profiles')
      .update({
        chart_data: null,
        chart_calculated_at: null
      })
      .eq('id', profileId);

    if (error) {
      console.error('[ChartDataCache] Error clearing cache:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err: any) {
    console.error('[ChartDataCache] Exception clearing cache:', err);
    return { success: false, error: err.message || 'Unknown error' };
  }
}

/**
 * Check if profile has valid cached data without loading it
 * Useful for UI indicators (e.g., "Cached" badge)
 * 
 * @param profileId - UUID of birth profile
 * @returns Boolean indicating if valid cache exists
 */
export async function hasValidChartDataCache(
  profileId: string
): Promise<boolean> {
  try {
    if (!profileId || profileId === 'my_profile') {
      return false;
    }

    const { data, error } = await supabase
      .from('birth_profiles')
      .select('chart_data, chart_calculated_at')
      .eq('id', profileId)
      .single();

    if (error || !data || !data.chart_data) {
      return false;
    }

    const chartData = data.chart_data as any;
    
    return (
      isChartDataValid(chartData) &&
      isChartDataCurrent(chartData) &&
      isChartDataFresh(chartData.calculated_at)
    );
  } catch (err) {
    return false;
  }
}

/**
 * Get cache metadata without loading full data
 * Useful for admin/debug views
 * 
 * @param profileId - UUID of birth profile
 * @returns Metadata about cache status
 */
export async function getChartDataCacheInfo(
  profileId: string
): Promise<{
  exists: boolean;
  version?: string;
  calculated_at?: string;
  age_hours?: number;
  size_bytes?: number;
  valid: boolean;
} | null> {
  try {
    if (!profileId || profileId === 'my_profile') {
      return null;
    }

    const { data, error } = await supabase
      .from('birth_profiles')
      .select('chart_data, chart_calculated_at')
      .eq('id', profileId)
      .single();

    if (error || !data) {
      return null;
    }

    if (!data.chart_data) {
      return {
        exists: false,
        valid: false
      };
    }

    const chartData = data.chart_data as any;
    const jsonString = JSON.stringify(chartData);
    const ageMs = Date.now() - new Date(chartData.calculated_at || 0).getTime();
    const ageHours = ageMs / (1000 * 60 * 60);

    return {
      exists: true,
      version: chartData.version,
      calculated_at: chartData.calculated_at,
      age_hours: Math.round(ageHours * 10) / 10,
      size_bytes: jsonString.length,
      valid: isChartDataValid(chartData) && isChartDataCurrent(chartData)
    };
  } catch (err) {
    console.error('[ChartDataCache] Error getting cache info:', err);
    return null;
  }
}

// END FILE: src/utils/chartDataCache.ts
